import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { markPlayerPromoNotificationRead } from 'reducers/playerPromos';
import { markPromoNotificationRead, PromoNotification } from 'reducers/user';
import { getAccessToken } from 'utils/auth';
import { SOCKET_SERVER_URL } from 'utils/urls';

// Single source of truth for notification data — the bell (NotificationCenter)
// and the account menu (AccountMenuPanel) both read the same Redux-backed list
// and share the same mark-as-read behaviour (see EN-9838 QA: the menu was
// rendering a static empty state instead of the real notifications).

function getEngineApiBase(): string {
  return (SOCKET_SERVER_URL || '')
    .replace(/\/+$/, '')
    .replace(/^wss:/, 'https:')
    .replace(/^ws:/, 'http:');
}

function normalizeNotifications(notifications: PromoNotification[]): PromoNotification[] {
  const seen = new Set<number>();

  return notifications
    .filter((notification) => notification && typeof notification.id === 'number')
    .filter((notification) => {
      if (seen.has(notification.id)) return false;
      seen.add(notification.id);
      return true;
    });
}

export interface UseNotificationsResult {
  sortedNotifications: PromoNotification[];
  unreadNotifications: PromoNotification[];
  unreadCount: number;
  markNotificationRead: (notification: PromoNotification) => Promise<void>;
}

export function useNotifications(): UseNotificationsResult {
  const dispatch = useAppDispatch();
  const moneyNotifications = useAppSelector((state) => state.user.promoNotifications || []);
  const promoNotifications = useAppSelector(
    (state) => state.playerPromos?.promoNotifications || []
  );

  const notifications = useMemo(
    () =>
      normalizeNotifications(
        promoNotifications.length > 0 ? promoNotifications : moneyNotifications
      ),
    [moneyNotifications, promoNotifications]
  );

  const sortedNotifications = useMemo(
    () => [...notifications].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0) || b.id - a.id),
    [notifications]
  );

  const unreadNotifications = useMemo(
    () => sortedNotifications.filter((notification) => notification.isRead === 0),
    [sortedNotifications]
  );

  const markAsReadRemote = useCallback(async (notificationId: number) => {
    if (notificationId < 0) return;

    const jwt = getAccessToken();
    const url = `${getEngineApiBase()}/api/readNotification`;

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${jwt}`
        },
        body: JSON.stringify({
          id: notificationId.toString(),
          token: jwt,
          jwt
        })
      });
    } catch (error) {
      console.error('[Notifications] Failed to mark notification as read:', error);
    }
  }, []);

  const markNotificationRead = useCallback(
    async (notification: PromoNotification) => {
      dispatch(markPromoNotificationRead(notification.id));
      dispatch(markPlayerPromoNotificationRead(notification.id));
      await markAsReadRemote(notification.id);
    },
    [dispatch, markAsReadRemote]
  );

  return {
    sortedNotifications,
    unreadNotifications,
    unreadCount: unreadNotifications.length,
    markNotificationRead
  };
}
